/* eslint-disable */
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
  deleteDoc,
  collection,
  getDocs,
  query,
  where,
  limit,
  deleteField,
} from "firebase/firestore";
import { db } from "../firebase";

const GOOGLE_SHEET_LIVE_KEYS = [
  "googleShit",
  "googleSheet",
  "googleSheetId",
  "googleSheetName",
  "googleSheetColumns",
  "useGoogleAPI",
  "sheetabndn",
  "frenchs",
  "arabics",
  "numberS",
];

const BACKUP_KEY = "googleSheetDhdBackup";

function pickSheetConfig(data) {
  const backup = {};
  for (const key of GOOGLE_SHEET_LIVE_KEYS) {
    if (data[key] !== undefined) {
      backup[key] = data[key];
    }
  }
  return backup;
}

function clearedSheetFields() {
  return {
    googleShit: "",
    googleSheet: "",
    googleSheetId: "",
    googleSheetName: "",
    googleSheetColumns: [],
    useGoogleAPI: false,
    sheetabndn: [],
    frenchs: false,
    arabics: false,
    numberS: false,
  };
}

export async function findStoreIdByUserId(userId) {
  if (!userId) return null;
  const snap = await getDocs(
    query(collection(db, "store"), where("user", "==", userId), limit(1)),
  );
  if (snap.empty) return null;
  return snap.docs[0].id;
}

export async function pauseGoogleSheetForDhd(storeId) {
  if (!storeId) return;

  const storeRef = doc(db, "store", storeId);
  const snap = await getDoc(storeRef);
  if (!snap.exists()) return;

  const data = snap.data();

  if (data[BACKUP_KEY] && typeof data[BACKUP_KEY] === "object") {
    await updateDoc(storeRef, clearedSheetFields());
    return;
  }

  const backup = pickSheetConfig(data);
  const hasAnything = Object.keys(backup).some((k) => {
    const v = backup[k];
    if (v === "" || v === false || v == null) return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  });

  if (!hasAnything) {
    await updateDoc(storeRef, clearedSheetFields());
    return;
  }

  await updateDoc(storeRef, {
    [BACKUP_KEY]: backup,
    ...clearedSheetFields(),
  });
}

export async function resumeGoogleSheetAfterDhd(storeId) {
  if (!storeId) return;

  const storeRef = doc(db, "store", storeId);
  const snap = await getDoc(storeRef);
  if (!snap.exists()) return;

  const data = snap.data();
  const backup = data[BACKUP_KEY];

  if (!backup || typeof backup !== "object") return;

  await updateDoc(storeRef, {
    ...clearedSheetFields(),
    ...backup,
    [BACKUP_KEY]: deleteField(),
  });
}

export async function deactivateDhdPartnership(
  userId,
  accountId,
  currentSubscription,
) {
  if (!userId) throw new Error("userId is required");
  if (!accountId) throw new Error("accountId is required");

  const liveRef = doc(db, "dhd_form", userId);
  const liveSnap = await getDoc(liveRef);

  if (liveSnap.exists()) {
    const liveData = liveSnap.data();

    const rejectedRef = doc(collection(db, "dhd_form_rejected"));
    await setDoc(rejectedRef, {
      ...liveData,
      userId,
      status: "deactivated",
      rejectionReason: "Désactivé par l'utilisateur",
      deactivatedBy: userId,
      deactivatedAt: serverTimestamp(),
      rejectedAt: serverTimestamp(),
      reviewedAt: serverTimestamp(),
      reviewedBy: userId,
    });

    await deleteDoc(liveRef);
  }

  return restorePreviousPlan(userId, accountId, currentSubscription);
}

async function restorePreviousPlan(userId, accountId, currentSubscription) {
  const sub = { ...(currentSubscription || {}) };
  const restored = sub.previousPlan || "الاساسية";

  sub.plan = restored;
  sub.previousPlan = null;
  sub.dhdPartnership = false;
  sub.dhdPartnershipActive = false;

  await updateDoc(doc(db, "account", accountId), { subscription: sub });

  try {
    const storeId = await findStoreIdByUserId(userId);
    if (storeId) {
      await resumeGoogleSheetAfterDhd(storeId);
    }
  } catch (e) {
    console.error("Failed to resume Google Sheet after DHD deactivate:", e);
  }

  return sub;
}

export function resolveDhdStatus(form) {
  if (!form) return "none";
  if (form.status === "approved") return "approved";
  if (form.finished === true) return "pending";
  return "in_progress";
}

export async function getDhdForm(userId) {
  if (!userId) return null;
  const uid = typeof userId === "string" ? userId : userId?.uid;
  if (!uid) throw new Error("A valid userId is required");

  try {
    const snap = await getDoc(doc(db, "dhd_form", uid));
    if (snap.exists()) return { ...snap.data(), id: snap.id };
  } catch (error) {
    // Ownership rules can deny a direct read of a missing document.
    // A query constrained to the owner can safely return an empty result.
    if (error.code !== "permission-denied") throw error;
  }

  const snapshot = await getDocs(
    query(collection(db, "dhd_form"), where("user", "==", uid), limit(1)),
  );
  if (snapshot.empty) return null;
  const snap = snapshot.docs[0];
  return { ...snap.data(), id: snap.id };
}

export async function getLatestApplication(userId) {
  return getDhdForm(userId);
}

export async function createNewApplication(userId, extra = {}) {
  if (!userId) throw new Error("userId is required");

  const ref = doc(db, "dhd_form", userId);
  const payload = {
    user: userId,
    userEmail: extra.userEmail ?? null,
    acceptDHD: false,
    pdfDownloaded: false,
    currentStep: 1,
    finished: false,
    status: null,
    idCardFrontUrl: null,
    idCardBackUrl: null,
    registrationFormUrl: null,
    idCardFrontUploaded: false,
    idCardBackUploaded: false,
    registrationFormUploaded: false,
    selectedCompanies: [],
    state: null,
    wilaya_code: null,
    rating: null,
    rejectionReason: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    ...extra,
  };

  await setDoc(ref, payload);
  return { id: userId, ...payload };
}

export async function updateApplication(userId, applicationId, data = {}) {
  if (!userId) return;

  const ref = doc(db, "dhd_form", applicationId || userId);
  await updateDoc(ref, {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function getUserDhdStatus(userId) {
  if (!userId) return { status: "none", form: null };

  const form = await getDhdForm(userId);
  if (!form) return { status: "none", form: null };

  return {
    status: resolveDhdStatus(form),
    form,
  };
}

export async function patchDhdWarning(userId, warningId, patch) {
  if (!userId || !warningId) return;

  const form = await getDhdForm(userId);
  if (!form || !Array.isArray(form.warnings)) return;

  const warnings = form.warnings.map((w) =>
    w.id === warningId ? { ...w, ...patch } : w,
  );

  await updateDoc(doc(db, "dhd_form", form.id || userId), {
    warnings,
    updatedAt: serverTimestamp(),
  });
}

export async function markDhdWarningSeen(userId, warningId) {
  await patchDhdWarning(userId, warningId, {
    seenAt: Timestamp.now(),
  });
}

export async function markDhdWarningSkipped(userId, warningId) {
  await patchDhdWarning(userId, warningId, {
    skippedAt: Timestamp.now(),
    seenAt: Timestamp.now(),
  });
}

export function pickActiveDhdWarning(warnings) {
  if (!Array.isArray(warnings) || warnings.length === 0) return null;

  const sorted = [...warnings].sort((a, b) => {
    const ta = a?.createdAt?.seconds ?? a?.createdAt?.toMillis?.() ?? 0;
    const tb = b?.createdAt?.seconds ?? b?.createdAt?.toMillis?.() ?? 0;
    return tb - ta;
  });

  for (const w of sorted) {
    if (!w?.id) continue;
    if (w.canSkip) {
      if (!w.skippedAt) return w;
    } else {
      return w;
    }
  }
  return null;
}
